<template>
	<div
		class="footer"
		:class="{ 'pointer-events-none': isCameraActive }"
		v-show="!(tutorialActive && tutorialStep < 5)"
	>
		<div class="footer__content">
			<div class="navigation">
				<div
					v-for="(item, index) in navigationMenuItemsComputed"
					:key="item.name"
					class="navigation__item"
					:class="[
						item.backgroundName,
						{
							disabled:
								(item.disabledTutorial &&
									tutorialActive &&
									tutorialStep < 9) ||
								item.disabled,
						},
						{
							'navigation__item--active': item.isActive,
						},
					]"
					@click="handleRoute(item)"
				>
					<div class="navigation__item-content">
						<div class="navigation__item-icon-wrapper">
							<AppNotification
								v-if="item.notification() > 0"
								class="navigation__item__notification"
								:value="item.notification()"
								:type="StatusTypes.WARNING"
							/>
							<div
								class="navigation__item-icon"
								:class="item.iconName"
							/>
						</div>
						<div v-if="showMenuTexts" class="navigation__item-name">
							{{ translate(item.text) }}
						</div>
					</div>
					<MenuTileBackground
						:state="
							item.isActive
								? MenuTileState.ACTIVE
								: item.backgroundName
						"
						:class="[
							`menu-tile-bg--${index + 1}`,
							{
								'menu-tile-bg--royal-provision':
									isRoyalProvisionActive,
							},
						]"
					/>
				</div>
			</div>

			<div
				class="safe-area"
				:class="[
					{ 'safe-area--royal-provision': isRoyalProvisionActive },
				]"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import MenuTileBackground from "@/components/SVG/MenuTileBackground.vue";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { UIAudioNames } from "@/game/entities/config/audio/audioConfig";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import { OfferProvider } from "@/services/OfferProvider";
import useAchievementsStore from "@/store/useAchievementsStore";
import useQuestStore from "@/store/useQuestStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import { MenuTileState, RouteNames, StatusTypes } from "@/types/ui";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	nextTick,
	onMounted,
	reactive,
	ref,
	watch,
} from "vue";
import type { RouteRecordNameGeneric } from "vue-router";
import { useRouter } from "vue-router";

const emitter = markRaw(useEmitter());
const tilerepo = markRaw(useTileRepository());

interface NavigationMenuItem {
	name: string;
	text: string;
	iconName: string;
	backgroundName: MenuTileState;
	isActive: boolean;
	audioName: UIAudioNames;
	disabledTutorial: boolean;
	disabled?: boolean;
	notification: () => number;
}

const router = useRouter();

const soundService = markRaw(SoundService.getInstance());

const runtimeStore = useRuntimeStore();
const { isCameraActive, infoNotifications } = storeToRefs(runtimeStore);

const questStore = useQuestStore();
const { activeQuests, tutorialActive, tutorialStep } = storeToRefs(questStore);

const userStore = useUserStore();
const { newUpgradesIds, isRoyalProvisionActive } = storeToRefs(userStore);

const settlementsStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementsStore);

const settingsStore = useSettingsStore();
const { showMenuTexts } = storeToRefs(settingsStore);

const achievementsStore = useAchievementsStore();
const { getUnclaimedAchievements } = storeToRefs(achievementsStore);

const entity = tilerepo.getTileEntityByTileCoordinates({
	x: 512,
	y: 512,
}) as SettlementCastleBuildingTileEntity | null;

const questNotificationsCount = (): number => {
	return activeQuests.value.filter((quest: QuestActive): boolean => {
		if (quest.type === QuestTypes.OFFER) return false;
		return quest.progress >= getAmount(quest.objective.amount);
	}).length;
};

const upgradesNotificationsCount = (): number => {
	return newUpgradesIds.value.length;
};

const shopNotificationsCount = (): number => {
	const notifications = [
		!!OfferProvider.freeRegularOfferAvailable,
		!!OfferProvider.freeEventOfferAvailable,
	];

	return notifications.filter((isActive: boolean): boolean => isActive)
		.length;
};

const infoNotificationsCount = (): number => {
	return infoNotifications.value + getUnclaimedAchievements.value.length;
};

const navigationMenuItems = reactive([
	{
		name: RouteNames.QUESTS,
		text: "ui.quests",
		iconName: "quests",
		backgroundName: MenuTileState.BASE,
		isActive: false,
		audioName: UIAudioNames.BUTTON_ULOHY,
		disabledTutorial: true,
		notification: questNotificationsCount,
	},
	{
		name: RouteNames.UPGRADES,
		text: "ui.upgrades",
		iconName: "upgrades",
		backgroundName: MenuTileState.BASE,
		isActive: false,
		audioName: UIAudioNames.BUTTON_VYLEPSENIA,
		disabledTutorial: true,
		notification: upgradesNotificationsCount,
	},
	{
		name: RouteNames.SHOP,
		text: "ui.shop",
		iconName: "shop",
		backgroundName: MenuTileState.PREMIUM,
		isActive: false,
		audioName: UIAudioNames.BUTTON_OBCHOD,
		disabledTutorial: true,
		notification: shopNotificationsCount,
	},
	{
		name: RouteNames.INFO,
		text: "ui.info",
		iconName: "menu-info",
		backgroundName: MenuTileState.BASE,
		isActive: false,
		audioName: UIAudioNames.BUTTON_INFO,
		disabledTutorial: true,
		notification: infoNotificationsCount, //TODO: new Achievements count
	},
	{
		name: RouteNames.SETTINGS,
		text: "ui.settings",
		iconName: "settings",
		backgroundName: MenuTileState.BASE,
		isActive: false,
		audioName: UIAudioNames.BUTTON_NASTAVENIA,
		disabledTutorial: true,
		notification: (): number => 0,
	},
]);

const navigationMenuItemsComputed = computed((): NavigationMenuItem[] => {
	return navigationMenuItems.map(
		(item: NavigationMenuItem): NavigationMenuItem => ({
			...item,
			iconName: item.isActive
				? `icon-${item.iconName}-56`
				: `icon-${item.iconName}-48`,
			backgroundName: item.isActive
				? MenuTileState.ACTIVE
				: item.backgroundName,
		}),
	);
});

/**
 * Tomasova potreba :D
 * @param item Navigation menu item to handle route change
 */
const handleRoute = (item: NavigationMenuItem): void => {
	if (
		(item.disabledTutorial &&
			tutorialActive.value &&
			tutorialStep.value < 9) ||
		item.disabled
	)
		return;
	playAudio(item.audioName);
	if (item.isActive) {
		router.push({
			name: RouteNames.LAYOUT_VIEW,
		});
	} else {
		router.push({ name: item.name, query: { from: "footer" } });
	}
};

const playAudio = (audioName: UIAudioNames): void => {
	soundService.playUIAudio(audioName);
};

const updateNavigationMenuItems = (name: RouteRecordNameGeneric): void =>
	navigationMenuItems.forEach((item: NavigationMenuItem): void => {
		item.isActive = item.name === name;
		item.backgroundName = item.isActive
			? MenuTileState.ACTIVE
			: item.iconName === "shop"
				? MenuTileState.PREMIUM
				: MenuTileState.BASE;
	});

watch(
	(): RouteRecordNameGeneric => router.currentRoute.value.name,
	updateNavigationMenuItems,
);

const childHeight = ref<number>(0);
watch(childHeight, (newValue: number): void => {
	document
		.getElementById("app")!
		.style.setProperty(
			"--footer-height",
			`calc(${newValue}px + var(--safe-area-inset-bottom))`,
		);
});

if (tutorialActive.value) {
	watch(
		(): [number, boolean] => [tutorialStep.value, tutorialActive.value],
		(): void => {
			if (tutorialStep.value >= 4 && tutorialActive.value) {
				nextTick((): void => {
					// Recalculate navigation after tutorial step change
					recalculateNavigation();
				});
			}
		},
	);
}

const parentElement = ref<HTMLElement | null>(null);
const children = ref<HTMLElement[]>([]);

const recalculateNavigation = (): void => {
	updateNavigationMenuItems(router.currentRoute.value.name);

	parentElement.value = document.querySelector(".navigation");
	children.value = Array.from(
		document.querySelectorAll(".navigation__item"),
	) as HTMLElement[];

	if (!parentElement.value || !children.value.length) return;

	children.value.forEach((element: HTMLElement): void => {
		element.style.removeProperty("height");
	});

	// NOTE: to ensure correct sizing when menu labels are hidden, after removing fixed dimensions
	nextTick((): void => {
		const parentWidth = parentElement.value?.offsetWidth ?? 0;
		const childrenCount = 5;

		const childWidth = parentWidth / childrenCount;
		// NOTE: 87px width, 76px height in Zeplin
		childHeight.value = showMenuTexts.value
			? (childWidth / 87) * 76
			: children.value[0].offsetHeight;

		// NOTE: This is very important, do not remove this
		children.value.forEach((element: HTMLElement): void => {
			Object.assign(element.style, {
				width: `${childWidth}px`,
				height: `${childHeight.value}px`,
			});
		});
	});
};

onMounted((): void => {
	recalculateNavigation();
});

watch(
	(): number | null => entity?.questProgressValues?.currentValue ?? null,
	(value): void => {
		if (
			!value &&
			!tutorialActive.value &&
			mainLevel.value > 1 &&
			mainLevel.value < 5
		)
			return;
		emitter.emit(EmitterEvents.SettlementQuestUpdate);
	},
);

watch(
	(): boolean => showMenuTexts.value,
	(): void => {
		recalculateNavigation();
	},
);
</script>

<style lang="scss" scoped>
.footer {
	display: flex;
	position: absolute;
	bottom: 0;
	left: 0;
	justify-content: center;
	align-items: center;
	z-index: 1;
	background-color: #434444;
	width: 100%;

	&::before {
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		z-index: 0;
		background: url($path-images + "backgrounds/textures/top-menu-texture-bg.avif")
			repeat center;
		background-size: 9.375rem auto;
		width: 100%;
		height: 100%;
		content: "";
	}

	&__content {
		position: relative;
		width: 100%;
		max-width: 27rem;
		@include flex-col-center;

		.navigation {
			display: flex;
			position: relative;
			flex-direction: row;
			z-index: 0;
			width: 100%;

			&__item {
				display: block;
				position: relative;
				text-align: center;
				text-decoration: none;

				&.disabled {
					opacity: 0.5;
					pointer-events: none;
				}

				&-content {
					position: relative;
					top: 0;
					left: 0;
					z-index: 1;
					padding: 0.125rem;
					width: 100%;
					height: 100%;
				}

				.menu-tile-bg {
					position: absolute;
					top: 0;
					left: 0;
					z-index: 0;
					width: 100%;
					height: 100%;
				}

				&--active {
					.navigation {
						&__item-icon {
							margin-top: -1rem;
						}

						&__item-name {
							color: #372615;
						}
					}
				}

				&-icon {
					transition: all 0.15s ease;
				}

				&-icon-wrapper {
					height: 3rem;
				}

				&-name {
					display: block;
					margin-top: 0.25rem;
					width: 100%;
					color: #fff3dd;
					font-weight: 800;
					font-size: 0.625rem;
					line-height: 0.625rem;
					text-align: center;
					text-transform: uppercase;
					@include text-truncate;
				}

				&__notification {
					position: absolute;
					top: 0.1875rem;
					right: 0.1875rem;
				}
			}
		}

		.safe-area {
			width: 100%;
			@include background(
				url($path-backgrounds + "menu/menu-bottom-safe-zone.avif"),
				cover,
				top center,
				no-repeat
			);
			@include safe-area-bottom;

			&--royal-provision {
				@include background(
					url($path-backgrounds + "menu/menu-bottom-safe-zone--royal-provision.avif?v2"),
					cover,
					top center,
					no-repeat
				);
			}
		}
	}
}
</style>
